'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import {
  CheckoutFragment,
  CartFragment,
  cartService,
  CartItemCheckoutFragment,
  CartItemFragment
} from '@/fo/api/magento';
import InfoIcon from '@/fo/icons/base/icons/info.svg';
import { UiKitPriceBase } from '@/fo/ui/kit/price/base';
import { UiKitTooltipBase } from '@/fo/ui/kit/tooltip/base';
import { UiLoyaltyLabelBase } from '@/fo/ui/loyalty/label/base';
import { UiOrderSummaryCompact } from '@/fo/ui/order/summary/compact';
import { utilEventCartModal } from '@/fo/util/event-cart-modal';

import { styles } from './FeatureCheckoutSummaryCompact.styles';

export interface FeatureCheckoutSummaryCompactProps {
  data: CheckoutFragment | CartFragment;
  items: CartItemCheckoutFragment[];
  onMainButtonClick?: () => void;
  isAuthorized?: boolean;
  isCart?: boolean;
  buttonText?: string;
  /** Показывать sticky-кнопку, когда основная кнопка не видна в viewport */
  onStickyButtonVisibilityChange?: (visible: boolean) => void;
}

export function FeatureCheckoutSummaryCompact({
  data,
  items,
  onMainButtonClick,
  isAuthorized,
  isCart = false,
  buttonText,
  onStickyButtonVisibilityChange
}: FeatureCheckoutSummaryCompactProps) {
  const slots = styles();
  const t = useTranslations();
  const mainButtonRef = useRef<HTMLButtonElement>(null);

  const [promoMessage, setPromoMessage] = useState<string>('');
  const [promoStatus, setPromoStatus] = useState<'success' | 'error' | ''>(
    data.applied_coupon?.code ? 'success' : ''
  );
  const [promoValue, setPromoValue] = useState<string>(
    data.applied_coupon?.code || ''
  );
  const [isLoadingPromo, setIsLoadingPromo] = useState<boolean>(false);

  const discounts = data.prices?.discounts ?? [];
  const totalPrice = data.prices?.grand_total?.value ?? 0;
  const subtotalPrice = data.prices?.subtotal_excluding_tax?.value ?? 0;
  //const currency = data.prices?.subtotal_excluding_tax?.currency ?? undefined;
  const itemLabels = () => (
    <>
      {t('orders.totals.products')}
      <span className={slots.unitsLabel()}>
        {items.length} {t('product.inStock.units')}
      </span>
    </>
  );
  const bonusesAfterOrder = data.total_bonus_points_info?.earned_amount ?? 0;
  const bonuses =
    data.total_bonus_points_info?.available_amount_for_current_cart ?? 0;

  const onPromoResponse = (
    { status, message }: { status: 'success' | 'error'; message: string },
    value = ''
  ) => {
    setPromoMessage(message || '');
    setPromoStatus(status);
    setPromoValue(status === 'success' ? value : '');
    setIsLoadingPromo(false);
    utilEventCartModal.mutate();
  };

  const onPromoSubmit = async (value: string) => {
    if (!value.trim()) return;

    setIsLoadingPromo(true);

    const result = await cartService.applyCouponToCart(value);

    onPromoResponse(
      result as { status: 'success' | 'error'; message: string },
      value
    );
  };

  const onPromoClear = async () => {
    setIsLoadingPromo(true);

    const result = await cartService.removeCouponFromCart();

    onPromoResponse(result as { status: 'success' | 'error'; message: string });

    setPromoStatus('');
  };

  const handleMainButtonClick = async () => {
    onMainButtonClick?.();
  };

  useEffect(() => {
    if (!isCart || !onStickyButtonVisibilityChange) return;

    const button = mainButtonRef.current;
    if (!button) return;

    const updateStickyVisibility = (entry: IntersectionObserverEntry) => {
      if (entry.isIntersecting) {
        onStickyButtonVisibilityChange(false);
        return;
      }

      const viewportBottom = entry.rootBounds?.bottom ?? window.innerHeight;
      onStickyButtonVisibilityChange(
        entry.boundingClientRect.top > viewportBottom
      );
    };

    const observer = new IntersectionObserver(
      ([entry]) => updateStickyVisibility(entry),
      { threshold: 0 }
    );

    observer.observe(button);

    return () => observer.disconnect();
  }, [isCart, onStickyButtonVisibilityChange]);

  const getDiscountValue = (discount: {
    amount: { value?: number | null };
    bonus_applied?: boolean;
  }) => {
    if (data.applied_coupon || !discount.bonus_applied)
      return <UiKitPriceBase value={discount.amount?.value ?? 0} />;

    return <UiLoyaltyLabelBase points={discount.amount?.value ?? 0} />;
  };

  const getDiscountLabel = (discount: {
    label: string;
    bonus_applied?: boolean;
  }) => {
    if (data.applied_coupon || !discount.bonus_applied) return discount.label;

    return (
      <p className={slots.bonusesLabel()}>
        {discount.label}
        <UiKitTooltipBase
          side="bottom"
          content={
            <div className={slots.tooltip()}>
              {t('Note for pay by bonuses')}
            </div>
          }
        >
          <InfoIcon className={slots.infoIcon()} />
        </UiKitTooltipBase>
      </p>
    );
  };

  return (
    <UiOrderSummaryCompact
      items={[
        {
          label: itemLabels(),
          value: <UiKitPriceBase value={subtotalPrice} />
        },
        ...discounts
          .filter(
            (discount): discount is NonNullable<typeof discount> =>
              discount !== null
          )
          .map((discount) => ({
            label: getDiscountLabel(discount),
            value: getDiscountValue(discount)
          })),
        ...(bonusesAfterOrder > 0 && isAuthorized && !isCart
          ? [
              {
                label: (
                  <p className={slots.bonusesLabel()}>
                    {t('bonuses-after-order-title')}
                    <UiKitTooltipBase
                      side="bottom"
                      content={
                        <div className={slots.tooltip()}>
                          {t('Note for bonuses')}
                        </div>
                      }
                    >
                      <InfoIcon className={slots.infoIcon()} />
                    </UiKitTooltipBase>
                  </p>
                ),
                value: <UiLoyaltyLabelBase points={bonusesAfterOrder} />
              }
            ]
          : []),
        ...('shipping_addresses' in data &&
        data.shipping_addresses?.[0]?.selected_shipping_method?.amount?.value !=
          null
          ? [
              {
                label: t('Delivery'),
                value:
                  data.shipping_addresses?.[0]?.selected_shipping_method?.amount
                    ?.value === 0 ? (
                    t('Free')
                  ) : (
                    <UiKitPriceBase
                      value={
                        data.shipping_addresses?.[0]?.selected_shipping_method
                          ?.amount?.value ?? 0
                      }
                    />
                  )
              }
            ]
          : [])
      ]}
      totalPrice={totalPrice}
      subtotalPrice={subtotalPrice}
      buttonText={buttonText || t('orderButton')}
      onClick={handleMainButtonClick}
      hasSpendBonuses={
        !!data.total_bonus_points_info?.spent_amount_for_current_cart &&
        data.total_bonus_points_info?.spent_amount_for_current_cart > 0
      }
      canSpendBonuses={
        !!data.total_bonus_points_info?.available_amount_for_current_cart &&
        data.total_bonus_points_info?.available_amount_for_current_cart > 0
      }
      bonuses={bonuses}
      promo={{
        value: promoValue,
        message: promoMessage,
        success: promoStatus === 'success',
        error: promoStatus === 'error',
        loading: isLoadingPromo
      }}
      onPromoSubmit={(value: string) => onPromoSubmit(value)}
      onPromoClear={onPromoClear}
      certificate={null}
      isAuthorized={isAuthorized}
      products={items as CartItemFragment[]}
      orderTitle={isCart ? '' : t('Your order')}
      allowButton={isCart}
      allowProducts={!isCart}
      allowPromo={isCart}
      variant={isCart ? 'cartDrawer' : 'checkout'}
      mainButtonRef={isCart ? mainButtonRef : undefined}
    />
  );
}
